import { useState } from 'react';
import { useLang } from 'hooks/lang';

import { actions, projectStatusConfig, getServiceName } from 'entities';

import {
  Alert,
  Switch,
  LabeledTextarea,
  LabeledSelect,
  Column,
  Row,
  Button,
  Label,
  Card,
  Box,
} from 'components/Blocks';
import { EarthIcon } from 'components/Icons';

import { ServiceOutsourcedTag } from 'pages/Projects/components';

import { useProjectProfile } from '../../context';
import { useFormControl } from 'hooks';

import { COUNTRIES } from 'utils/countries';

export function RequestCountryForm() {
  const { t } = useLang();
  const { project, updateProject } = useProjectProfile();

  const { state, dispatch } = useFormControl({
    justification: '',
    country: '',
    demographicsComment: '',
  });

  const [servicesCosts, setServicesCosts] = useState<Record<string, boolean>>(
    project.services.reduce((acc, { id }) => ({ ...acc, [id]: false }), {}),
  );

  const toggleServiceCosts = (id: string): void => {
    setServicesCosts((old) => ({
      ...old,
      [id]: !old[id],
    }));
  };

  const missingFilling = [
    !state.country.trim(),
    !state.justification.trim(),
    !state.demographicsComment.trim(),
    Object.values(servicesCosts).every((value) => !value),
  ].some(Boolean);

  const requestCountry = actions.projects.requestProjectCountryAddition.use({
    block: missingFilling,

    params: {
      ...state,
      project: project.id,
      servicesToInclude: Object.entries(servicesCosts)
        .filter(([_, value]) => !!value)
        .map(([service]) => service),
    },

    onSuccess: (requestData) => {
      updateProject((old) => ({
        ...old,
        addCountryRequest: requestData,
        status: projectStatusConfig.pendingApproval.code,
      }));
    },
  });

  const canAddCountry = Number(new Date(project.expectedDate as string)) > Number(new Date());

  if (!canAddCountry)
    return (
      <Alert level="info" type="lightly-filled">
        {t(
          'there-are-no-dates-available-to-add-a-new-country-please-adjust-project-date-before-proceeding',
        )}
      </Alert>
    );

  return (
    <Column gap="6">
      <Row gap="2" flexWrap="wrap">
        <span>{t('countries-already-included')}:</span>

        {project.countries.map((country) => (
          <Box className="rounded-md bg-primary px-2 py-0.5 text-primary-foreground" key={country}>
            {country}
          </Box>
        ))}
      </Row>

      <LabeledSelect
        id="countries"
        options={COUNTRIES.filter(({ value }) => !project.countries.includes(value))}
        value={state.country}
        onChangeDirect={(country) => dispatch({ country })}
        label={t('choose-country')}
      />

      {!!state.country && (
        <Column gap="2">
          <span className="font-medium">{t('services-costs')}:</span>

          {project.services.map(({ id, service, outsourceConfig }) => (
            <Card flow="row" key={`service-${id}`} justifyContent="space-between">
              <Row gap="2" fullWidth={false}>
                <span>{getServiceName(id, project.tasks)}</span>

                {!!outsourceConfig && <ServiceOutsourcedTag />}
              </Row>

              <Label className="ml-auto phone:hidden">{t('request-inclusion')}</Label>

              <Switch
                id={id}
                checked={servicesCosts[service]}
                onChange={() => toggleServiceCosts(id)}
                className="phone:ml-auto"
              />
            </Card>
          ))}
        </Column>
      )}

      <LabeledTextarea
        label={t('demographics-comment') as string}
        name="demographicsComment"
        value={state.demographicsComment}
        onChange={({ target }) => dispatch({ demographicsComment: target.value })}
      />

      <LabeledTextarea
        label={t('justification') as string}
        name="justification"
        value={state.justification}
        onChange={({ target }) => dispatch({ justification: target.value })}
      />

      <Button disabled={missingFilling} {...requestCountry.buttonProps}>
        <EarthIcon />
        {t('add-country')}
      </Button>
    </Column>
  );
}
